import type { PointerSpot } from './pointer'

// The box is kept in the pane's content coordinates (page 1 at the top, whatever is scrolled),
// so the corner the drag started from stays on the same page while the pane creeps.
export interface HighlightBox {
  left: number
  top: number
  width: number
  height: number
}

export interface ContentSpot {
  x: number
  y: number
}

export const spotInContent = (
  container: HTMLElement,
  pointer: PointerSpot,
): ContentSpot => {
  const frame = container.getBoundingClientRect()
  return {
    x: pointer.x - frame.left + container.scrollLeft,
    y: pointer.y - frame.top + container.scrollTop,
  }
}

export const boxBetween = (
  from: ContentSpot,
  to: ContentSpot,
): HighlightBox => ({
  left: Math.min(from.x, to.x),
  top: Math.min(from.y, to.y),
  width: Math.abs(to.x - from.x),
  height: Math.abs(to.y - from.y),
})

// Where to paint it: back in screen coordinates and cut to the pane, so the highlighter never
// spills over the map above while the pointer is dragged past the top of the pages.
export const boxOnScreen = (
  container: HTMLElement,
  box: HighlightBox,
): HighlightBox | null => {
  const frame = container.getBoundingClientRect()
  const left = Math.max(
    box.left - container.scrollLeft + frame.left,
    frame.left,
  )
  const top = Math.max(box.top - container.scrollTop + frame.top, frame.top)
  const right = Math.min(
    box.left + box.width - container.scrollLeft + frame.left,
    frame.right,
  )
  const bottom = Math.min(
    box.top + box.height - container.scrollTop + frame.top,
    frame.bottom,
  )
  // A straight-down drag reports the same x the whole way, so a flat box still has to paint.
  if (right < left || bottom < top) return null
  return { left, top, width: right - left, height: bottom - top }
}

export const sameBox = (one: HighlightBox | null, other: HighlightBox | null) =>
  one === other ||
  (one !== null &&
    other !== null &&
    one.left === other.left &&
    one.top === other.top &&
    one.width === other.width &&
    one.height === other.height)
